import React, { useEffect, useState } from 'react';
import { AlertCircle, Clock, X } from 'lucide-react';

interface TimeoutWarningModalProps {
  isOpen: boolean;
  onKeepAlive: () => void;
  onTimeout: () => void;
  countdownSeconds?: number;
}

export const TimeoutWarningModal: React.FC<TimeoutWarningModalProps> = ({
  isOpen,
  onKeepAlive,
  onTimeout,
  countdownSeconds = 60,
}) => {
  const [secondsLeft, setSecondsLeft] = useState(countdownSeconds);

  useEffect(() => {
    if (!isOpen) return;

    setSecondsLeft(countdownSeconds);

    const interval = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          onTimeout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen, countdownSeconds, onTimeout]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-navy/30 backdrop-blur-md animate-in fade-in duration-300">
      <div className="bg-surface-container-lowest/80 backdrop-blur-md shadow-ambient rounded-3xl w-full max-w-sm border border-outline-variant/10 overflow-hidden flex flex-col scale-in animate-in zoom-in-95 duration-200">
        
        {/* Header */}
        <header className="px-6 py-5 flex items-center justify-between">
          <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center text-amber-500">
            <AlertCircle size={20} />
          </div>
          <button onClick={onKeepAlive} className="p-1 hover:bg-surface-container rounded-lg transition-colors">
            <X size={18} className="text-secondary" />
          </button>
        </header>

        {/* Content */}
        <div className="px-6 pb-6 space-y-2">
          <h3 className="text-lg font-bold font-display text-navy">Session expiring soon</h3>
          <p className="text-xs text-secondary leading-relaxed">
            Due to inactivity, your session will automatically log out in:
          </p>
          <div className="flex items-center gap-2 py-3 justify-center bg-surface-container-low rounded-2xl border border-surface-variant/10">
            <Clock size={18} className="text-amber-500 animate-pulse" />
            <span className="text-xl font-bold font-display text-navy">{secondsLeft} seconds</span>
          </div>
        </div>

        {/* Footer */}
        <footer className="px-6 py-4 bg-surface-container-low flex items-center gap-3 border-t border-surface-variant/10">
          <button 
            onClick={onKeepAlive}
            className="flex-1 py-2.5 bg-gradient-to-r from-primary to-primary-container text-on-primary text-xs font-bold rounded-xl shadow-button hover:opacity-90 active:scale-95 transition-all flex items-center justify-center gap-2"
          >
            Keep Session Alive
          </button>
        </footer>
      </div>
    </div>
  );
};
